import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Client } from "@/features/clients/types";
import type { FormSubmission } from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox } from "./formik-controls";

// --- Form IA 2848 editable payload (co-located; keys mirror the backend mapper
// `apps/forms/mappings/form_ia2848.py` `build_defaults` exactly — passed verbatim,
// no snake→camel). Taxpayer + rep contact identity is filled server-side.

export type Ia2848GenerateFor = "taxpayer" | "spouse";

/** Iowa ID-type radio export states. */
export type Ia2848IdType = "" | "SSN" | "PTIA" | "CAF" | "IAN";

export interface Ia2848Representative {
  /** The backing Practitioner id (contact details are looked up from it server-side). */
  practitioner: string;
  active: boolean;
  name: string;
  /** Iowa tax account number; when set it prints instead of the PTIN. */
  ia_account: string;
}

export interface Form_ia2848_Data {
  generate_for: Ia2848GenerateFor;
  representatives: Ia2848Representative[];
  // Single Iowa tax matter (repeated across every printed rep row server-side).
  tax_type1: string;
  tax_permit1: string;
  tax_mmyy_from1: string;
  tax_mmyy_to1: string;
  // Exclusions (exception_a..exception_g).
  exception_a: boolean;
  exception_b: boolean;
  exception_c: boolean;
  exception_d: boolean;
  exception_e: boolean;
  exception_f: boolean;
  exception_g: boolean;
  // Source-of-authority flags.
  auth_poa: boolean;
  auth_guardian: boolean;
  auth_ch680: boolean;
  auth_attorney: boolean;
  auth_executor: boolean;
  auth_trustee: boolean;
  auth_ind_corp: boolean;
  auth_parent: boolean;
  auth_gov_rep: boolean;
  auth_successor: boolean;
  // Refund-check recipient block.
  refund_rep: string;
  refund_address: string;
  refund_city: string;
  refund_state: string;
  refund_zip: string;
  // Non-taxpayer signer block.
  signer_name: string;
  signer_id: string;
  signer_id_type: Ia2848IdType;
  signer_address: string;
  signer_city: string;
  signer_state: string;
  signer_zip: string;
  signer_phone: string;
  signer_email: string;
  signer_firm: string;
  signer_title: string;
}

const GENERATE_FOR_OPTIONS: { value: Ia2848GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
];

const EXCEPTIONS: { name: keyof Form_ia2848_Data; label: string }[] = [
  { name: "exception_a", label: "(a) Add or substitute representatives" },
  { name: "exception_b", label: "(b) Disclose confidential tax information" },
  { name: "exception_c", label: "(c) Execute waivers / consents to assessment" },
  { name: "exception_d", label: "(d) Execute closing agreements" },
  { name: "exception_e", label: "(e) Delegate authority" },
  { name: "exception_f", label: "(f) Receive refund checks" },
  { name: "exception_g", label: "(g) Sign returns" },
];

const AUTHORITIES: { name: keyof Form_ia2848_Data; label: string }[] = [
  { name: "auth_poa", label: "General or Durable Power of Attorney" },
  { name: "auth_guardian", label: "Guardian, Conservator, or Custodian appointed by Court" },
  { name: "auth_ch680", label: "Receiver appointed" },
  { name: "auth_attorney", label: "Licensed attorney" },
  { name: "auth_executor", label: "Executor or personal representative" },
  { name: "auth_trustee", label: "Trustee" },
  { name: "auth_ind_corp", label: "Individual" },
  { name: "auth_parent", label: "Parent or guardian" },
  { name: "auth_gov_rep", label: "Government representative" },
  { name: "auth_successor", label: "Successor" },
];

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse"]).required(),
  tax_type1: Yup.string().max(120, "Too long"),
  tax_permit1: Yup.string().max(40, "Too long"),
});

function toInitialValues(data: Record<string, unknown>): Form_ia2848_Data {
  const d = data as Partial<Form_ia2848_Data>;
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    tax_type1: d.tax_type1 ?? "",
    tax_permit1: d.tax_permit1 ?? "",
    tax_mmyy_from1: d.tax_mmyy_from1 ?? "",
    tax_mmyy_to1: d.tax_mmyy_to1 ?? "",
    exception_a: d.exception_a ?? false,
    exception_b: d.exception_b ?? false,
    exception_c: d.exception_c ?? false,
    exception_d: d.exception_d ?? false,
    exception_e: d.exception_e ?? false,
    exception_f: d.exception_f ?? false,
    exception_g: d.exception_g ?? false,
    auth_poa: d.auth_poa ?? false,
    auth_guardian: d.auth_guardian ?? false,
    auth_ch680: d.auth_ch680 ?? false,
    auth_attorney: d.auth_attorney ?? false,
    auth_executor: d.auth_executor ?? false,
    auth_trustee: d.auth_trustee ?? false,
    auth_ind_corp: d.auth_ind_corp ?? false,
    auth_parent: d.auth_parent ?? false,
    auth_gov_rep: d.auth_gov_rep ?? false,
    auth_successor: d.auth_successor ?? false,
    refund_rep: d.refund_rep ?? "",
    refund_address: d.refund_address ?? "",
    refund_city: d.refund_city ?? "",
    refund_state: d.refund_state ?? "",
    refund_zip: d.refund_zip ?? "",
    signer_name: d.signer_name ?? "",
    signer_id: d.signer_id ?? "",
    signer_id_type: d.signer_id_type ?? "",
    signer_address: d.signer_address ?? "",
    signer_city: d.signer_city ?? "",
    signer_state: d.signer_state ?? "",
    signer_zip: d.signer_zip ?? "",
    signer_phone: d.signer_phone ?? "",
    signer_email: d.signer_email ?? "",
    signer_firm: d.signer_firm ?? "",
    signer_title: d.signer_title ?? "",
  };
}

function GenerateForField() {
  const { values, setFieldValue } = useFormikContext<Form_ia2848_Data>();
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={values.generate_for}
      onValueChange={(v) => setFieldValue("generate_for", v as Ia2848GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/**
 * IA 2848 representatives — the client's assigned practitioners (from the
 * client profile). The user toggles which are active on this Iowa POA and edits
 * each one's Iowa tax account number; contact details come from the practitioner
 * record server-side. Iowa prints up to three; reps 4+ spill to a continuation
 * sheet server-side.
 */
function Ia2848RepresentativesTable() {
  const { values } = useFormikContext<Form_ia2848_Data>();
  const reps = values.representatives;

  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No representatives are assigned to this client. Add them on the client's
        profile, then reopen this form.
      </p>
    );
  }

  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Active</TableHead>
            <TableHead>Rep Name</TableHead>
            <TableHead className="w-56">Iowa Tax Account No.</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.map((rep, i) => (
            <TableRow key={rep.practitioner || i}>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.active`}
                  aria-label={`Active ${rep.name}`}
                />
              </TableCell>
              <TableCell className="font-medium">{rep.name}</TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.ia_account`}
                  placeholder="Optional (else PTIN prints)"
                  aria-label={`Iowa tax account number for ${rep.name}`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

function FieldRow({ label, name, ...props }: { label: string; name: string } & Record<string, unknown>) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={name}>{label}</Label>
      <FormikCell name={name} {...props} />
    </div>
  );
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Iowa Form IA 2848 (Power of Attorney) — prefilled from the client +
 * its practitioners. Iowa deltas over the base POA skeleton: up to three
 * representatives each carrying an Iowa tax account number, a single tax matter
 * (repeated across rep rows), exclusion checkboxes (a–g), a source-of-authority
 * checklist, a refund-check recipient block, and a non-taxpayer signer block.
 */
export function Form_ia2848_Editor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Power of Attorney Given To</SectionHeading>
          <Ia2848RepresentativesTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Tax Matter</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldRow label="Tax Types or Other Matters" name="tax_type1" placeholder="e.g. Individual Income" />
            <FieldRow label="Iowa Tax Account or Permit Number" name="tax_permit1" />
            <FieldRow label="Beginning Tax Period (MMYY)" name="tax_mmyy_from1" placeholder="e.g. 0120" />
            <FieldRow label="Ending Tax Period (MMYY)" name="tax_mmyy_to1" placeholder="e.g. 1223" />
          </div>
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Exclusions</SectionHeading>
          <div className="grid gap-3 sm:grid-cols-2">
            {EXCEPTIONS.map((e) => (
              <FormikCheckbox key={e.name} name={e.name} label={e.label} />
            ))}
          </div>
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Source of Authority</SectionHeading>
          <div className="grid gap-3 sm:grid-cols-2">
            {AUTHORITIES.map((a) => (
              <FormikCheckbox key={a.name} name={a.name} label={a.label} />
            ))}
          </div>
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Refund Check Recipient</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldRow label="Representative to receive check(s)" name="refund_rep" />
            <FieldRow label="Address" name="refund_address" />
            <FieldRow label="City" name="refund_city" />
            <FieldRow label="State" name="refund_state" />
            <FieldRow label="ZIP" name="refund_zip" />
          </div>
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Non-Taxpayer Signer (optional)</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldRow label="Name" name="signer_name" />
            <FieldRow label="Firm or Company Legal Name" name="signer_firm" />
            <FieldRow label="Identification Number" name="signer_id" />
            <FieldRow label="Address" name="signer_address" />
            <FieldRow label="City" name="signer_city" />
            <FieldRow label="State" name="signer_state" />
            <FieldRow label="ZIP" name="signer_zip" />
            <FieldRow label="Phone" name="signer_phone" />
            <FieldRow label="Email" name="signer_email" />
          </div>
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
